import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, expand, map, tap } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { v4 as uuidv4 } from 'uuid';

const API_ENDPOINT = 'api/';

@Injectable({
  providedIn: 'root',
})
export class ApiCartService {

  private sessionId: string|null = null;
  public cart: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public cartModalOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Constructor
   *
   * @param {HttpClient} http
   */
  public constructor(private http: HttpClient) {
    if(localStorage.getItem('sessionId') != null) {
      this.sessionId = localStorage.getItem('sessionId');
    } else {
      this.getNewSessionId();
    }
  }

  private getNewSessionId() {
    this.sessionId = uuidv4();
    this.postCheckSessionId().pipe(
        tap( _ => {
          localStorage.setItem('sessionId', this.sessionId);
          this.getCart().subscribe();
        }),
        expand((data) => {
          if(!data) {
            this.sessionId = uuidv4();
            return this.postCheckSessionId();
          } else {
            return EMPTY;
          }
        }
      )
    ).subscribe();
  }

  public reloadSessionId() {
    this.getNewSessionId();
  }

  /**
   * Returns the session id
   * 
   * @returns string
   */
  public getSessionId() {
    return this.sessionId;
  }

  /**
   * Api call to get cart data
   *
   * @return {Observable<any>}
   */
   public getCart(): Observable<any> {
    let headers = new HttpHeaders({
      'Cache-Control':  'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
      'Pragma': 'no-cache',
      'Expires': '0'
    });

    return this.http.get(API_ENDPOINT + 'shopping_cart/get/' + this.sessionId, {
      headers
    })
    .pipe(
      map((response: any) => {
        this.cart.next(response.shoppingCart);
      }),
      catchError((error) => {
          return throwError(error);
        },
      ),
    );
  }

  /**
   * Api call to get order data
   *
   * @return {Observable<any>}
   */
  public getOrder(sessionId: string|boolean = false): Observable<any> {
    let headers = new HttpHeaders({
      'Cache-Control':  'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
      'Pragma': 'no-cache',
      'Expires': '0'
    });

    let sessionIdToUse = sessionId != false ? sessionId : this.sessionId;
    return this.http.get(API_ENDPOINT + 'order/get/' + sessionIdToUse, {
      headers
    })
    .pipe(
      map((response: any) => {
        return response.order;
      }),
      catchError((error) => {
          return throwError(error);
        },
      ),
    );
  }

  /**
   * Api call to add product to the cart
   *
   * @return {Observable<any>}
   */
  public postCheckSessionId(): Observable<any> {
    return this.http.post(API_ENDPOINT + 'shopping_cart/post/check_session_id', {
      sessionId: this.sessionId,
    }).pipe(
      map((response: any) => {
        return response.valid;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(error.error);
      }),
    );
  }

  /**
   * Api call to add product to the cart
   *
   * @return {Observable<any>}
   */
  public addProductToShoppingCart(product:any, quantity: number, size: string): Observable<any> {
    return this.http.post(API_ENDPOINT + 'shopping_cart/post/add_product', {
      sessionId: this.sessionId,
      productId: product.id,
      quantity: quantity,
      size: size
    }).pipe(
      map((response: any) => {
        this.cart.next(response.shoppingCart);
        return response.shoppingCart;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(error.error);
      }),
    );
  }

  /**
   * Api call to remove a product from the cart
   * 
   * @param cartProduct 
   * @returns {Observable<any>}
   */
  public removeProductToShoppingCart(productId: string, size: string): Observable<any> {
    return this.http.post(API_ENDPOINT + 'shopping_cart/post/remove_product', {
      sessionId: this.sessionId,
      productId: productId,
      size: size,
    }).pipe(
      map((response: any) => {
        this.cart.next(response.shoppingCart);
        return response.shoppingCart;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(error.error);
      }),
    );
  }

  /**
   * Api call to remove a product from the cart
   * 
   * @param cartProduct 
   * @returns {Observable<any>}
   */
   public applyCouponCode(couponCode: string): Observable<any> {
    return this.http.post(API_ENDPOINT + 'shopping_cart/post/set_coupon', {
      sessionId: this.sessionId,
      couponCode: couponCode,
    }).pipe(
      map((response: any) => {
        if(response.valid) {
          this.cart.next(response.shoppingCart);
        }
        return response;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(error.error);
      }),
    );
  }

  /**
   * Api call to remove a product from the cart
   * 
   * @param cartProduct 
   * @returns {Observable<any>}
   */
  public removeCouponCode(): Observable<any> {
    return this.http.post(API_ENDPOINT + 'shopping_cart/post/remove_coupon', {
      sessionId: this.sessionId,
    }).pipe(
      map((response: any) => {
        this.cart.next(response.shoppingCart);
        return response;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(error.error);
      }),
    );
  }

  /**
   * Api calls to update the cart product's quantities
   * 
   * @returns {Observable<any>}
   */
  public updateProductsToShoppingCart(): Observable<any> {
    return this.http.post(API_ENDPOINT + 'shopping_cart/post/update_products', {
      sessionId: this.sessionId,
      cartData: this.cart.value,
    }).pipe(
      map((response: any) => {
        this.cart.next(response.shoppingCart);
        return response.shoppingCart;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(error.error);
      }),
    );
  }

  public saveOrderShippingData(gateway, formData: any) {
    return this.http.post(API_ENDPOINT + 'shopping_cart/post/order_data', {
      sessionId: this.sessionId,
      formData,
      gateway,
    })
    .pipe(
      map((response: any) => {
        this.cart.next(response.shoppingCart);
        return response;
      }),
      catchError((error) => {
          return throwError(error);
        },
      ),
    );
  }

  public requestCheckout() {
    return this.http.post(API_ENDPOINT + 'shopping_cart/checkout', {
      sessionId: this.sessionId,
    })
    .pipe(
      map((response: any) => {
        return response.url;
      }),
      catchError((error) => {
          return throwError(error);
        },
      ),
    );
  }

  public savePayPalOrderData(data) {
    return this.http.post(API_ENDPOINT + 'paypal/post/order_data', {
      sessionId: this.sessionId,
      data: data,
    })
    .pipe(
      map((response: any) => {
        return response.order;
      }),
      catchError((error) => {
          return throwError(error);
        },
      ),
    );
  }
}
